import React, { useEffect, useState } from "react";
import {
  buddyCheckboxQuestion_1,
  buddyTableRatingRows,
  tableRatingOptions,
  textAreaQuestions,
} from "./form-data/buddyFeedback-data";
import CheckboxGroupInput from "./common-components/CheckboxInput";
import TextareaInput from "./common-components/TextAreaInput";
import InlineTextInput from "./common-components/InlineTextInput";
import BreadCrumbForm from "./common-components/BreadCrumbForm";
import FormHeading from "./common-components/FormHeading";
import useApi from "../../core/customhook/useApi";
import InlineDateField from "./common-components/InlineDateField";
import Swal from "sweetalert2";
import { BuddyFeedbackErrorProps, validateBuddyForm } from "./validation/form-validations/Buddy-validation";
import { all_routes } from "../router/all_routes";
import { fetch_buddy_feedback, get_prefilled_details } from "../../environment";
import Loading from "../../core/common/custom-loading/Loading";

import dayjs from "dayjs";
import { formattedDate } from "../../core/common/formattedDate";
import { toast } from "react-toastify";
import ViewSignature from "../../core/common/ViewSignature";
import ESignaturePad from "../../core/common/ESignaturePad";

export type BuddyFormProps = {
  employee_id: string;
  name: string;
  dateOfJoining: string;
  buddyId?: string;
  buddyName: string;
  welcomeOnFirstDay: string;
  helpedGelWithTeam: string;
  buddyResolvedConcerns: string;
  satisfiedWithBuddy: string;
  knowledgeRating: string;
  approachabilityRating: string;
  availabilityRating: string;
  mostHelpful: string;
  couldDoBetter: string;
  signatureDate?: string;
  signature_url?: string;
  signature?: File | null;
};

const initialFormData = {
  employee_id: "",
  name: "",
  dateOfJoining: "",
  buddyName: "",
  welcomeOnFirstDay: "",
  helpedGelWithTeam: "",
  buddyResolvedConcerns: "",
  satisfiedWithBuddy: "",
  knowledgeRating: "",
  approachabilityRating: "",
  availabilityRating: "",
  mostHelpful: "",
  couldDoBetter: "",
  signatureDate: "",
  signature: null
};

const BuddyFeedbackForm = () => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<BuddyFormProps>(initialFormData);
  const [isAlreadySaved, setIsAlreadySaved] = useState(false)
  // const { EmployeeOptions } = useEmployeeHook();


  // API's
  const { callApi: getEmpDetailsApi, loading: empDetailsLoading } = useApi(
    get_prefilled_details,
    {
      method: "GET",
      autoCall: false,
    },
  );
  const { callApi: getSavedDataApi, loading: getFormLoading } = useApi(
    fetch_buddy_feedback,
    {
      method: "GET",
      autoCall: false,
    },
  );
  const {
    callApi: buddyFormApi,
    loading: submitLoading,
    error,
  } = useApi("/api/buddy/save", {
    method: "POST",
    autoCall: false,
  });



  const fetchEmpData = async () => {
    try {
      const res = await getEmpDetailsApi();
      if (res?.success) {
        const resData = res?.data || {};
        setFormData((prev) => ({
          ...prev,
          employee_id: resData?.employee_id ?? "",
          name: resData?.employee_name ?? "",
          dateOfJoining: resData?.join_date ?? "",
          buddyId: resData?.manager_id ?? "",
          buddyName: resData?.primary_manager_name ?? "",
          signatureDate: dayjs().format("YYYY-MM-DD")
        }));
      }
    } catch (error) {
      console.error("failed to fetch:", error);
    }
  };
  // useEffect(() => {
  //   fetchEmpData();
  // }, []);

  // fetch saved form data
  const fetchSavedData = async () => {
    try {
      const res = await getSavedDataApi();
      if (res?.success) {
        // console.log("resdata", res);
        const data = res?.data || null;
        if (!data) {
          fetchEmpData()
          return;
        };

        setIsAlreadySaved(true);

        setFormData((prev) => ({
          ...prev,
          name: data?.name ?? "",
          dateOfJoining: formattedDate(data?.date_of_joining) ?? "",
          buddyName: data?.buddy_name ?? "",
          welcomeOnFirstDay: data?.buddy_made_feel_welcome ?? "",
          helpedGelWithTeam: data?.buddy_helped_gel_with_team ?? "",
          buddyResolvedConcerns: data?.buddy_resolved_concerns_in_time ?? "",
          satisfiedWithBuddy: data?.satisfied_with_buddy_support ?? "",
          knowledgeRating: data?.buddy_knowledge_rating ?? "",
          approachabilityRating: data?.buddy_approachability_rating ?? "",
          availabilityRating: data?.buddy_availability_rating ?? "",
          mostHelpful: data?.most_helpful_about_buddy ?? "",
          couldDoBetter: data?.buddy_could_do_better ?? "",
          signatureDate: data?.signature_date ?? "",
          signature_url: data?.signature_path ?? ""
        }))
      }
    } catch (error) {
      console.error("failed to fetch:", error);
    }
  };
  useEffect(() => {
    fetchSavedData()
  }, [])

  const handleClearError = (name: keyof BuddyFormProps) =>
    setErrors((prev: any) => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });

  const handleSignatureChange = (
    field: keyof BuddyFormProps,
    file: File | null,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
    handleClearError(field);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: BuddyFormProps) => ({
      ...prev,
      [name]: value,
    }));
    handleClearError(name as keyof BuddyFormProps);
  };

  const handleDateChange = (name: string, value: string) => {
    const [day, month, year] = value.split("-");
    const formatted = `${year}-${month}-${day}`;
    setFormData((prev) => ({ ...prev, [name]: formatted }));
    handleClearError(name as keyof BuddyFormProps);
  };

  const handleFieldDataChange = (name: string, value: string) => {
    // console.log(name, value, label);
    setFormData((prev: BuddyFormProps) => ({
      ...prev,
      [name]: prev[name as keyof BuddyFormProps] == value ? "" : value,
    }));
    handleClearError(name as keyof BuddyFormProps);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { errors, firstError } = validateBuddyForm(formData);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      toast.error(firstError ?? "Please fill all required fields")
      // Swal.fire({
      //   icon: "error",
      //   title: "Validation Error!",
      //   text: firstError ?? "Please fill all required fields",
      //   confirmButtonText: "OK",
      //   confirmButtonColor: "#fd7e14",
      // });
      return;
    }

    // console.log(formData);
    const payload = {
      employee_id: formData?.employee_id ?? "",
      name: formData?.name ?? "",
      date_of_joining: dayjs(formData?.dateOfJoining, "DD/MM/YYYY").format("YYYY-MM-DD") ?? "",
      buddy_id: formData?.buddyId ?? "",
      buddy_name: formData?.buddyName ?? "",
      buddy_made_feel_welcome: formData?.welcomeOnFirstDay ?? "",
      buddy_helped_gel_with_team: formData?.helpedGelWithTeam ?? "",
      buddy_resolved_concerns_in_time: formData?.buddyResolvedConcerns ?? "",
      satisfied_with_buddy_support: formData?.satisfiedWithBuddy ?? "",
      buddy_knowledge_rating: formData?.knowledgeRating ?? "",
      buddy_approachability_rating: formData?.approachabilityRating ?? "",
      buddy_availability_rating: formData?.availabilityRating ?? "",
      most_helpful_about_buddy: formData?.mostHelpful ?? "",
      buddy_could_do_better: formData?.couldDoBetter ?? "",
      signature_date: formData?.signatureDate ?? "",
      signature: formData?.signature ?? ""
    };

    const formDataPayload = new FormData()

    Object.entries(payload).forEach(([key, value]) => {
      if (key === "signature" && value) {
        formDataPayload.append(key, value as File);
      } else if (value !== null && value !== undefined) {
        formDataPayload.append(key, String(value));
      }
    });

    try {
      const response = await buddyFormApi(formDataPayload);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Submitted!",
          text: "Form submitted successfully",
          confirmButtonText: "OK",
          confirmButtonColor: "#fd7e14",
        });
        // setFormData(initialFormData);
        fetchSavedData()
      }
      // console.log(response);
    } catch (error: any) {
      console.log("error message", error.response);
      const errorMsg = error.response?.data?.message ?? null;
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: errorMsg ?? "Failed to submit form data.",
        confirmButtonText: "OK",
        confirmButtonColor: "#fd7e14",
      });
    }
  };

  // console.log(formData.signature)
  // console.log(errors)

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <BreadCrumbForm
          title="Buddy Feedback Form"
          linkTitle="Buddy Assessment Form"
        />

        {(empDetailsLoading || getFormLoading) ? (
          <div className="card p-3">
            <Loading />
          </div>
        ) : (
          <>
            {/* Form content */}
            <div className="card shadow-sm mt-4 form-card-container">
              <div className="card-body p-4 p-md-5">
                <form onSubmit={handleSubmit}>
                  {/* Title */}
                  <FormHeading title="Buddy Feedback Form" />

                  {/* Basic Info */}
                  <div>
                    <InlineTextInput
                      label="Employee Name:"
                      name="name"
                      value={formData?.name ?? ""}
                      onChange={handleChange}
                      errors={errors}
                      disabled
                      classStyle="text-capitalize"
                    />
                    <InlineTextInput
                      label="Date of Joining:"
                      name="dateOfJoining"
                      value={formData?.dateOfJoining ?? ""}
                      onChange={handleChange}
                      errors={errors}
                      disabled
                    />
                    <InlineTextInput
                      label="Buddy Name:"
                      name="buddyName"
                      value={formData?.buddyName ?? ""}
                      onChange={handleChange}
                      disabled
                      errors={errors}
                    />

                  </div>
                  <hr />

                  <div className="mt-4">
                    {buddyCheckboxQuestion_1?.map((ques) => {
                      return (
                        <CheckboxGroupInput
                          key={ques.name}
                          label={ques?.label}
                          name={ques?.name}
                          options={ques?.options}
                          value={formData[ques.name as keyof Omit<BuddyFormProps, "signature">] ?? ""}
                          onChange={handleFieldDataChange}
                          errors={errors}
                          isDisabled={isAlreadySaved}
                        />
                      );
                    })}
                    <hr />

                    {/* Rating Table */}
                    <div className="mb-4 table-rating-input">
                      <p className="fw-semibold mb-3 form-dark-text">
                        Please rate the following:
                      </p>
                      <div className="table-responsive">
                        <table className="table table-bordered text-center align-middle">
                          <thead className="table-light">
                            <tr>
                              <th
                                className="text-start"
                                style={{ minWidth: 220 }}
                              ></th>
                              {tableRatingOptions?.map((r) => (
                                <th key={r} style={{ fontSize: "0.8rem" }}>
                                  {r}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {buddyTableRatingRows?.map((row) => (
                              <tr key={row.name}>
                                <td className="text-start form-dark-text">
                                  <p className="pb-0 mb-1">{row.label}</p>
                                  {errors[row?.name]?.length > 0 && (
                                    <p className=" text-danger fs-12 ">
                                      {errors[row?.name]}
                                    </p>
                                  )}
                                </td>
                                {tableRatingOptions?.map((opt) => (
                                  <td key={opt}>
                                    <input
                                      type="checkbox"
                                      name={row.name}
                                      value={opt}
                                      className="form-check-input"
                                      checked={
                                        formData[
                                        row.name as keyof BuddyFormProps
                                        ] === opt
                                      }
                                      // errors={errors}
                                      onChange={() =>
                                        handleFieldDataChange(row.name, opt)
                                      }
                                      disabled={isAlreadySaved}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <hr />

                    {/* Open-ended */}
                    {textAreaQuestions?.map((ques) => {
                      return (
                        <TextareaInput
                          key={ques.name}
                          label={ques.label}
                          name={ques.name}
                          rows={ques.rows}
                          value={formData[ques.name as keyof Omit<BuddyFeedbackErrorProps, "signature">]}
                          onChange={handleChange}
                          errors={errors}
                          isReadOnly={isAlreadySaved}
                        />
                      );
                    })}
                    <hr />

                    {/* Signature */}
                    <div className="mb-3">
                      {/* {!isAlreadySaved ? <ViewSignature fileSource={typeof formData?.signature == 'string' ? formData?.signature : ""} /> : <> */}
                      <label htmlFor="signature" className="form-label fw-semibold">
                        Signature of Employee:
                      </label>
                      {isAlreadySaved ? <ViewSignature fileSource={formData?.signature_url ?? ""} /> :
                        <ESignaturePad
                          field="signature"
                          onChange={handleSignatureChange}
                          existingSignatureUrl={
                            typeof formData.signature === "string"
                              ? formData.signature // URL string from API (edit mode)
                              : null
                          }
                          error={errors.signature}
                          isDisabled={submitLoading}
                        />
                      }
                    </div>

                    <InlineDateField
                      label="Date:"
                      name="signatureDate"
                      value={formData?.signatureDate}
                      onChange={handleDateChange}
                      disabled={true}
                    />
                  </div>

                  <div className="text-end">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitLoading || isAlreadySaved}
                    >
                      {submitLoading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            {/* /Form content */}
          </>
        )}
      </div>
      <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
        <p className="mb-0"> © {new Date().getFullYear()} WeConnect. All rights reserved.</p>
      </div>
    </div>
  );
};

export default BuddyFeedbackForm;
{/* <InlineDateField
                        label="Date of Joining:"
                        name="dateOfJoining"
                        value={formData?.dateOfJoining}
                        onChange={handleDateChange}
                        errors={errors}
                        disabled
                      /> */}
{/* <SearchableSelect
                        label="Buddy's Name:"
                        name="buddyId"
                        value={formData?.buddyId ?? ""}
                        options={EmployeeOptions}
                        // onChange={handleFieldDataChange}
                        onChange={(name, value, label) => {
                          setFormData((prev) => ({
                            ...prev,
                            buddyId: value ?? "",
                            buddyName: label ?? "",
                          }));
                        }}
                        hasRowGrid={true}
                        // errors={errors}
                      /> */}